/**
 * HomePage Component
 *
 * Displays search results based on URL query parameters.
 * Data is pre-loaded via loader (SSR-ready).
 *
 * MIGRATION NOTES:
 * ────────────────────────────────────────────────────────────────
 * This component was refactored to use React Router v7's data API.
 *
 * BEFORE (Client-Side Fetching):
 * - useState for experiences, loading, error
 * - useEffect to fetch data after component render
 * - Loading spinner shown while fetching
 * - Data fetching happens AFTER page load
 *
 * AFTER (Loader-Based Prefetching):
 * - useLoaderData to get pre-fetched data
 * - No loading state needed (data ready before render)
 * - No useEffect for data fetching
 * - Faster perceived performance
 *
 * BENEFITS:
 * - Eliminates loading spinners on initial page load
 * - Data available immediately (pre-fetched by homeLoader)
 * - SSR-ready (search engines see full content)
 * - Better user experience (no content flash)
 */

import React, { useState, useRef, useEffect } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MainSearch from '../../components/search/MainSearch';
import FilterPills from '../../components/search/FilterPills';
import ExperienceGrid from '../../components/experiences/ExperienceGrid';
import useURLStateManager from '../../hooks/useURLStateManager';

export default function HomePage() {
  // Data from loader (pre-fetched)
  const { experiences, searchMetadata, error } = useLoaderData();

  // Local state
  const [searchParams] = useSearchParams();
  const [selectedAdventureTypes, setSelectedAdventureTypes] = useState([]);
  const [showHeaderSearch, setShowHeaderSearch] = useState(false);
  const searchBarRef = useRef(null);

  // URL state manager for filter pills
  const urlStateManager = useURLStateManager();

  // Handle scroll for sticky header search
  useEffect(() => {
    const handleScroll = () => {
      if (searchBarRef.current) {
        const rect = searchBarRef.current.getBoundingClientRect();
        // Show header search when main search bar is scrolling out of view (1px threshold)
        setShowHeaderSearch(rect.top < 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header
        showSearch={showHeaderSearch}
        selectedTypes={selectedAdventureTypes}
        onTypesChange={setSelectedAdventureTypes}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div ref={searchBarRef}>
          <MainSearch variant="standalone" />
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 my-8">
            <h3 className="text-red-800 font-semibold mb-2">Search Error</h3>
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Search metadata */}
        {searchMetadata && !error && (
          <div className="mb-4 text-sm text-gray-600">
            <p>
              Found {searchMetadata.total} {searchMetadata.total === 1 ? 'result' : 'results'}
              {searchMetadata.metadata?.searchLocation && (
                <span> near {searchMetadata.metadata.searchLocation.displayAddress || searchMetadata.metadata.searchLocation.formattedAddress}</span>
              )}
            </p>
          </div>
        )}

        {/* Active Filter Pills */}
        <div className="mb-6">
          <FilterPills urlStateManager={urlStateManager} showClearAll={true} />
        </div>

        {/* Results grid */}
        {!error && (
          <ExperienceGrid experiences={experiences} />
        )}

        {/* No results state */}
        {!error && experiences.length === 0 && searchParams.toString() && (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600 mb-4">No adventures found matching your criteria</p>
            <button
              onClick={() => window.location.href = '/'}
              className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition"
            >
              View All Adventures
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
